import {
  createContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import type { User } from "@supabase/supabase-js";
import {
  supabase,
  TokenManager,
  type StoredTokenData,
} from "@/supabase/supabaseClient";
import type { UserProfile, PlatformRole } from "@/types/user";
import type { Role } from "@/types/rbac";
import { useTenant } from "@/hooks/useTenant";

interface AuthContextProps {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  signOut: () => Promise<void>;
  signIn: (
    email: string,
    password: string
  ) => Promise<{ success: boolean; error?: string }>;
  refreshAuth: () => Promise<void>;
  isAdmin: boolean;
  isAuthenticated: boolean;
  /** Check tenant-level role */
  hasRole: (roleName: string) => boolean;
  /** Check tenant-level roles */
  hasAnyRole: (roleNames: string[]) => boolean;
  getPrimaryRole: () => string | null;
  /** Check platform-level role (Super Admin, Platform Admin) */
  hasPlatformRole: (roleName: string) => boolean;
  /** Whether the user has any platform role */
  hasPlatformAccess: boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export { AuthContext };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const { tenantId } = useTenant();

  // Track whether signIn() already set the user+profile so initializeAuth
  // doesn't redundantly re-fetch everything on the same mount.
  const signedInThisSessionRef = useRef(false);

  // Check if user is authenticated based on localStorage token only
  const isAuthenticated = !!user && !TokenManager.isTokenExpired();

  // Function to sign in using email/password and store token
  const signIn = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      // Don't set loading here - let the Auth component handle it
      // setLoading(true);

      // Sign in with Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("Sign in error:", error);
        return { success: false, error: error.message };
      }

      if (data.session?.access_token && data.user) {
        // Store token in localStorage and set Supabase session
        const tokenData: StoredTokenData = {
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token || "",
          expires_at:
            Math.floor(Date.now() / 1000) + (data.session.expires_in || 3600),
          user: data.user,
        };
        await TokenManager.storeToken(tokenData);

        // Set user state immediately so the app can navigate
        setUser(data.user);
        signedInThisSessionRef.current = true;

        // Fetch user profile in background — don't block navigation
        fetchUserProfile(data.user.id, tenantId).then((profile) => {
          setUserProfile(profile);
        });

        return { success: true };
      }

      return { success: false, error: "No session data received" };
    } catch (error) {
      console.error("Sign in error:", error);
      return { success: false, error: "An unexpected error occurred" };
    }
    // Don't set loading false here - let the Auth component handle it
    // finally {
    //   setLoading(false);
    // }
  };

  // Function to sign out
  const signOut = async () => {
    try {
      // Clear localStorage token first
      TokenManager.clearToken();
      signedInThisSessionRef.current = false;

      // Clear state
      setUser(null);
      setUserProfile(null);

      // Sign out from Supabase (optional, since we rely on localStorage)
      await supabase.auth.signOut();

      // Redirect to signin page using window.location
      window.location.href = "/signin";
    } catch (error) {
      console.error("Error during sign out process:", error);
      // Still clear local state even if there was an error
      TokenManager.clearToken();
      setUser(null);
      setUserProfile(null);
      window.location.href = "/signin";
    }
  };

  // Fetch user profile with role information using localStorage token
  const fetchUserProfile = useCallback(
    async (
      userId: string,
      currentTenantId?: string | null
    ): Promise<UserProfile | null> => {
      try {
        const token = TokenManager.getStoredToken();
        if (!token || TokenManager.isTokenExpired()) {
          console.error("No valid token available for fetching user profile");
          return null;
        }

        const headers = {
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          ...TokenManager.getAuthHeader(),
        };

        // Fire all three requests in parallel
        const profilePromise = fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/user_profiles?select=id,email,first_name,last_name,avatar_url,created_at,updated_at&id=eq.${userId}`,
          { headers }
        );

        const rolesPromise = currentTenantId
          ? fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/user_roles?select=role_id,roles(id,name,description,is_system,is_active)&user_id=eq.${userId}&tenant_id=eq.${currentTenantId}`,
              { headers }
            )
          : null;

        const platformPromise = fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/platform_user_roles?select=role:role_id(id,name,description,is_active)&user_id=eq.${userId}`,
          { headers }
        );

        const [response, rolesResponse, platformResponse] = await Promise.all([
          profilePromise,
          rolesPromise,
          platformPromise,
        ]);

        if (response.status === 401) {
          await signOut();
          return null;
        }

        if (response.ok) {
          const userProfileData = await response.json();

          if (userProfileData.length > 0) {
            const rawProfile = userProfileData[0];

            // Parse tenant-scoped roles
            let rbacRoles: Role[] = [];
            if (rolesResponse?.ok) {
              const rolesData = await rolesResponse.json();
              rbacRoles = Array.isArray(rolesData)
                ? rolesData
                    .map((entry: { roles?: Role }) => entry.roles)
                    .filter((roleEntry: unknown): roleEntry is Role =>
                      Boolean(roleEntry)
                    )
                : [];
            }

            // Parse platform roles
            let platformRoles: PlatformRole[] = [];
            if (platformResponse.ok) {
              const platformData = await platformResponse.json();
              platformRoles = Array.isArray(platformData)
                ? platformData
                    .map((entry: { role?: PlatformRole }) => entry.role)
                    .filter((r: unknown): r is PlatformRole => Boolean(r))
                : [];
            }

            const profile: UserProfile = {
              id: rawProfile.id ?? "",
              email: rawProfile.email ?? "",
              first_name: rawProfile.first_name,
              last_name: rawProfile.last_name,
              avatar_url: rawProfile.avatar_url,
              tenant_id: rawProfile.tenant_id,
              created_at: rawProfile.created_at ?? new Date().toISOString(),
              updated_at: rawProfile.updated_at ?? new Date().toISOString(),
              rbac_roles: rbacRoles,
              platform_roles: platformRoles,
            };

            return profile;
          } else {
            console.warn("No user profile found for user ID:", userId);
            return null;
          }
        } else {
          console.error("Failed to fetch user profile:", await response.text());
          return null;
        }
      } catch (error) {
        console.error("Error in fetchUserProfile:", error);
        return null;
      }
    },
    [tenantId]
  );

  // Initialize authentication state from localStorage on mount
  useEffect(() => {
    // If signIn() already set user+profile this session, skip redundant fetch.
    // Only re-fetch profile when tenantId changes (for role resolution).
    if (signedInThisSessionRef.current) {
      if (user && tenantId) {
        // tenantId just resolved — refresh profile to pick up tenant roles
        fetchUserProfile(user.id, tenantId).then((profile) => {
          setUserProfile(profile);
        });
      }
      return;
    }

    const initializeAuth = async () => {
      try {
        const token = TokenManager.getStoredToken();

        if (!token || TokenManager.isTokenExpired()) {
          setLoading(false);
          return;
        }

        // Ensure token is ready and session is set
        const tokenReady = await TokenManager.ensureTokenReady();
        if (!tokenReady) {
          // Don't clear the token immediately - try to continue
        }

        // Get user data using the token (this will work even if session setting failed)
        const { data, error } = await supabase.auth.getUser(token);

        if (error || !data.user) {
          TokenManager.clearToken();
          setLoading(false);
          return;
        }

        setUser(data.user);

        // Fetch user profile
        const profile = await fetchUserProfile(data.user.id, tenantId);
        setUserProfile(profile);
      } catch (error) {
        console.error("Error initializing auth:", error);
        TokenManager.clearToken();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [fetchUserProfile, tenantId]);

  // Set up periodic token validation
  useEffect(() => {
    const validateTokenPeriodically = async () => {
      const token = TokenManager.getStoredToken();

      if (!token) {
        return;
      }

      if (TokenManager.isTokenExpired()) {
        await signOut();
        return;
      }

      // Optional: Validate with Supabase periodically
      const isValid = await TokenManager.validateTokenWithSupabase();
      if (!isValid) {
        await signOut();
      }
    };

    const interval = setInterval(validateTokenPeriodically, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  // Listen for token expiration events
  useEffect(() => {
    const handleTokenExpired = () => {
      signOut();
    };

    window.addEventListener("tokenExpired", handleTokenExpired);
    return () => window.removeEventListener("tokenExpired", handleTokenExpired);
  }, []);

  // Function to manually refresh authentication state (useful after OAuth)
  const refreshAuth = async (): Promise<void> => {
    try {
      const token = TokenManager.getStoredToken();
      if (!token || TokenManager.isTokenExpired()) {
        setUser(null);
        setUserProfile(null);
        return;
      }

      // Get user data using the token
      const { data, error } = await supabase.auth.getUser(token);
      if (error || !data.user) {
        TokenManager.clearToken();
        setUser(null);
        setUserProfile(null);
        return;
      }

      setUser(data.user);

      // Fetch user profile
      const profile = await fetchUserProfile(data.user.id, tenantId);
      setUserProfile(profile);
    } catch (error) {
      console.error("Error refreshing auth:", error);
      TokenManager.clearToken();
      setUser(null);
      setUserProfile(null);
    }
  };

  // Helper function to check if user has a specific role
  const hasRole = useCallback(
    (roleName: string): boolean => {
      if (!userProfile?.rbac_roles) return false;
      return userProfile.rbac_roles.some(
        (role) => role.name.toLowerCase() === roleName.toLowerCase()
      );
    },
    [userProfile]
  );

  // Helper function to check if user has any of the specified roles
  const hasAnyRole = useCallback(
    (roleNames: string[]): boolean => {
      if (!userProfile?.rbac_roles) return false;
      return userProfile.rbac_roles.some((role) =>
        roleNames.some((name) => name.toLowerCase() === role.name.toLowerCase())
      );
    },
    [userProfile]
  );

  // Helper function to get the primary role (first role in the array)
  const getPrimaryRole = useCallback((): string | null => {
    if (!userProfile?.rbac_roles || userProfile.rbac_roles.length === 0) {
      return null;
    }
    return userProfile.rbac_roles[0].name;
  }, [userProfile]);

  // Check platform-level role (separate from tenant RBAC)
  const hasPlatformRole = useCallback(
    (roleName: string): boolean => {
      if (!userProfile?.platform_roles) return false;
      return userProfile.platform_roles.some(
        (role) => role.name.toLowerCase() === roleName.toLowerCase()
      );
    },
    [userProfile]
  );

  // Whether the user has any platform role
  const hasPlatformAccess = !!(
    userProfile?.platform_roles && userProfile.platform_roles.length > 0
  );

  // Compute isAdmin based on tenant-level RBAC roles only
  const isAdmin = hasRole("Admin");

  const value = {
    user,
    userProfile,
    loading,
    setLoading,
    signOut,
    signIn,
    refreshAuth,
    isAdmin,
    isAuthenticated,
    hasRole,
    hasAnyRole,
    getPrimaryRole,
    hasPlatformRole,
    hasPlatformAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
